import type { REWARD_TYPE_ID } from '@modules/bonusManagement/types';
import type { AccumulatedConditions } from '@modules/common/accumulatedTriggerConditions/types';
import type { BetTypeDetails } from '@modules/common/betTypes/types';
import type { ICampaignCasinoGamesConditions } from '@modules/common/casinoConditions/types';
import type { OUTCOME_TYPE } from '@modules/common/outcomeNotifications/types';
import type { IConditionRow } from '@modules/common/sportsData/types';
import type { ITimeConditions } from '@modules/common/timeConditions/types';
import type {
  ICtaButton,
  ILocalizationDetails,
  ITermsAndConditionsRawLocale,
} from '@modules/common/types';
import type { Combinator, MODULE } from '@modules/types';
import type { IOption } from '@shared/form/types';
import type { Dayjs } from 'dayjs';

export enum TRIGGER_TYPE {
  CASINO_BET = 'CASINO_BET',
  DEPOSIT = 'DEPOSIT',
  LOGIN = 'LOGIN',
  REGISTRATION = 'REGISTRATION',
  SPORT_BET = 'SPORT_BET',
}
export enum BET_TRIGGER_ACTION_TYPE {
  COMBO_INSURANCE = 'COMBO_INSURANCE',
  PLACEMENT = 'PLACEMENT',
  SETTLEMENT = 'SETTLEMENT',
}

export type CampaignModule = Extract<MODULE, 'campaign' | 'automaticCampaign'>;

interface ICampaignBase {
  name: string;
  enabled: boolean;
  trigger: TRIGGER_TYPE;
  hasBrandsWithoutAccess: boolean;
}

export interface ICampaignGrid extends ICampaignBase {
  id: number;
  endDate: number;
  brands: string[];
  createdAt: number;
  createdBy: string;
  startDate: number;
  modifiedAt: number;
  modifiedBy: string;
  targetGroup: string;
  bonusNames: string[];
  hasRelation: boolean;
  segmentCondition: string;
  termsAndConditions?: string;
  targetedSegmentCount: number;
  targetedSegmentCalculatedAt?: null | number;
}

interface ICategory {
  id: number;
  name: string;
}

export interface ICampaign extends ICampaignBase {
  id: null | number;
  brands: IBrandId[];
  file?: Blob | File;
  description: string;
  targetGroup: string;
  bonusNames: string[];
  timeZone: null | string;
  uploadType: null | string;
  isVisibleToPlayer: boolean;
  isWelcomeCampaign: boolean;
  termsAndConditions: string;
  category: null | ICategory;
  productType?: null | string;
  playerLimits: IPlayerLimits;
  ctaButton: null | ICtaButton;
  betConditions: IBetConditions;
  campaignLimits: ICampaignLimits;
  eventContextRule: null | string;
  segmentCondition: null | string;
  timeConditions: ITimeConditions;
  isOverrideBonusTimezone: boolean;
  segmentContextRule: null | string;
  relatedCampaign: IRelatedCampaign;
  termsAndConditionsContent?: string;
  triggerEventContextRule: null | string;
  bonusTemplates: ICampaignBonusTemplate[];
  outcomeNotification: IOutcomeNotifications;
  isRemoveFromSegmentAfterActivation: boolean;
  accumulatedConditions: AccumulatedConditions;
  localizationDetails: null | ILocalizationDetails[];
  extraRewardBonusTemplates: ICampaignBonusTemplateBase[];
  casinoConditions: null | ICampaignCasinoGamesConditions;
  termsAndConditionsRaw?: null | string | ITermsAndConditionsRawLocale[];
}

export interface ICampaignSearch {
  page: number;
  count: number;
  totalCount: number;
  data: ICampaignGrid[];
}

export interface IRelatedCampaign {
  id: number;
  name: string;
  trigger?: string;
  timeRange: string;
}

interface IBonusTemplatePayload {
  id: number;
  type: string;
  time: null | string;
  bonusGrantingType: string;
  daysAfterActivation: null | number;
}

export interface ICampaignPayload extends Omit<
  ICampaignBase,
  'hasBrandsWithoutAccess'
> {
  timeZone: string;
  brandIds: number[];
  targetGroup: string;
  description?: string;
  files: null | File[];
  uploadType: null | string;
  productType?: null | string;
  id?: null | number | string;
  playerLimits: IPlayerLimits;
  campaignLimits: ICampaignLimits;
  segmentCondition: null | string;
  timeConditions: ITimeConditions;
  bonusTemplateIds: IBonusTemplatePayload[];
  outcomeNotification: IOutcomeNotifications;
  extraRewardBonusTemplates: Array<{ id: number; type: string }>;
}

type Limit = null | number | string;

export interface IPlayerLimits {
  daily?: Limit;
  total?: Limit;
  weekly?: Limit;
  monthly?: Limit;
  totalBonusesAmount?: Limit;
}

export interface ICampaignLimits {
  daily?: Limit;
  total?: Limit;
  weekly?: Limit;
  monthly?: Limit;
}

interface IBetConditions {
  betStatuses: string[];
  useIndividual: boolean;
  virtualMoneyBetType: string;
  betTypeDetails: BetTypeDetails;
  actionType: BET_TRIGGER_ACTION_TYPE;
  excludeBetsOutsideActivityPeriod: boolean;
  excludedSportsConditions: {
    combinator: Combinator;
    sportsDataConditionFields: IConditionRow[];
  };
  includedSportsConditions: {
    combinator: Combinator;
    sportsDataConditionFields: IConditionRow[];
  };
}

export interface IBrandId {
  id: number;
}

export type TargetGroup = Record<string, string>;

export interface ICampaignBonusTemplateBase {
  id: number;
  name: string;
  bonusType: string;
}

export interface ICampaignBonusTemplate extends ICampaignBonusTemplateBase {
  time: null | string;
  bonusGrantingType: string;
  brandIds: null | number[];
  daysAfterActivation?: null | number;
}

export interface ICampaignBonusTemplateRowBase {
  bonusType: string;
  bonusName: IOption;
}

export interface ICampaignBonusTemplateRow extends ICampaignBonusTemplateRowBase {
  bonusGrantingType: string;
  daysAfterActivation?: string;
  timeAfterActivation?: Dayjs | string;
}

export interface IApplicablePlayers {
  total: number;
  applicable: number;
}

export interface IRelationInfo {
  id: string;
}

export interface IAffiliateCode {
  value: string;
  isForceOverwrite: boolean;
}

export interface IOutcomeNotifications {
  outcomeType: string;
  affiliateCode: null | IAffiliateCode;
  bonusOfferExpirationTimeInMillis: null | number;
  templates: Partial<Record<OUTCOME_TYPE, number>>;
}

export interface IMessageTemplate {
  id: number;
  name: string;
}

export interface IGetRelatedCampaignsPayload {
  enabled?: null | boolean;
  brandIds: null | number[];
  triggers: null | string[];
  excludeIds: null | number[];
}

export interface IRelatedCampaignsResponse {
  campaigns: IRelatedCampaignNames[];
}

export interface IRelatedCampaignNames {
  id: number;
  name: string;
}

export interface IFreeSpinPayload {
  brandIds: number[];
  bonusType?: string;
  rewardTypeId?: REWARD_TYPE_ID;
}

export interface ISuperPotPayload {
  brandIds: number[];
}

export interface IGetCampaignNotificationTemplatesPayload {
  brandIds: number[];
  outcomeType: string;
}
